import { observer } from "mobx-react-lite";
import { useState } from "react";

import ChooseEntityDialog from "@/components/ChooseEntityDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ToastType } from "@/enums";
import {
  Entity,
  EntityType,
  type SmartTableTraceImportPayload,
  useStartSmartTableTraceImport,
} from "@/queries";
import { displayToast } from "@/utils/toast";

interface AddTracesToTableDialogProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  buildPayload: () => SmartTableTraceImportPayload | null;
  onEnqueueSuccess?: () => void;
}

type SmartTableEntityWithSelectedSheet = Entity<EntityType.SMART_TABLE> & {
  selected_sheet_id?: string;
  selected_sheet_title?: string;
};

const AddTracesToTableDialog = observer(
  ({ isOpen, setOpen, buildPayload, onEnqueueSuccess }: AddTracesToTableDialogProps) => {
    const [selectedTable, setSelectedTable] = useState<SmartTableEntityWithSelectedSheet>();
    const { mutateAsync: startTraceImport, isLoading: isImporting } =
      useStartSmartTableTraceImport();

    const selectedTableId = selectedTable?.id !== undefined ? String(selectedTable.id) : undefined;
    const targetSheetId = selectedTable?.selected_sheet_id;

    const canSubmit = !!selectedTableId && !!targetSheetId && !isImporting;

    const handleSelectTable = (table: Entity) => {
      setSelectedTable(table as SmartTableEntityWithSelectedSheet);
    };

    const handleConfirm = async () => {
      if (!selectedTableId) {
        displayToast("Please select a table", ToastType.error);
        return;
      }

      if (!targetSheetId) {
        displayToast("Selected table has no sheets", ToastType.error);
        return;
      }

      const payload = buildPayload();
      if (!payload) {
        displayToast("Could not build trace import payload", ToastType.error);
        return;
      }

      try {
        const response = await startTraceImport({
          tableId: selectedTableId,
          sheetId: targetSheetId,
          payload,
        });

        if (!response.success) {
          displayToast(response.message || "Could not queue trace import", ToastType.error);
          return;
        }

        displayToast(
          `Trace import queued for "${selectedTable?.name || "selected table"}"`,
          ToastType.success,
        );
        setOpen(false);
        onEnqueueSuccess?.();
      } catch {
        displayToast("Could not queue trace import", ToastType.error);
      }
    };

    return (
      <Dialog onOpenChange={setOpen} open={isOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Traces to Table</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-gray-600">Select a table to import the selected traces.</p>
            <ChooseEntityDialog
              type={EntityType.SMART_TABLE}
              onSelect={handleSelectTable}
              selectedEntity={selectedTable as Entity | undefined}
              currentFolderId={selectedTable?.folder_id ?? null}
              readonly={isImporting}
              config={{ selectSmartTableSheet: true }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isImporting}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} isLoading={isImporting} disabled={!canSubmit}>
              Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  },
);

export default AddTracesToTableDialog;
